import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import ProjectCreatedModal from 'components/shared/modal/ProjectCreatedModal';
import BuyCreditsModal from 'components/shared/BuyCreditsModal';
import HelpPanel from 'components/shared/HelpPanel';
import axiosPrivate from 'config/axiosPrivate';
import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CustomAutocomplete } from 'components/shared/DropDown/CustomAutocomplete';
import { CustomAutocompleteListAndFreeSolo } from 'components/shared/DropDown/CustomAutocompleteFreeSolo';
import { CustomChipsAutocomplete } from 'components/shared/DropDown/CustomChipsAutocomplete';
import TagInput from 'components/shared/TagInput';
import KnowledgeBaseUrlsInput from 'components/shared/KnowledgeBaseUrlsInput';
import { useHistory, useLocation } from 'react-router-dom';
import './textDisplay.css';
import './agentia.css';
import './createproject.css';
import Joyride, { STATUS, EVENTS, ACTIONS } from "react-joyride";
import 'shepherd.js/dist/css/shepherd.css';
import {
  Spinner,
  Tooltip,
  UncontrolledTooltip
} from 'reactstrap';
import { QuitProjectCreationModal } from 'components/shared/modal/QuitProjectCreationModal';
import usePageTracking from 'utils/hooks/usePageTracking';
import { formatWebsiteThematicsService } from 'utils/websiteThematics';

const MAX_KNOWLEDGE_BASE_FILES = 3;
const ADMIN_CLIENT_CREATE_AGENT_IA_PAGE = '/AdminClient/createagentiafromlist';
const GENERATE_TEMP_AGENT_ENDPOINT = '/smart-content/agent_ia/generate_temp_agent';
const getCompanyInfoFileUrl = (websiteId) =>
  `/project/website/${websiteId}/company-info-file`;

const normalizeWebsiteUrl = (input) =>
  (input || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '');

const findWebsiteByUrl = (websiteList, targetUrl) => {
  const normalizedTarget = normalizeWebsiteUrl(targetUrl);
  if (!normalizedTarget || !Array.isArray(websiteList)) return null;
  return (
    websiteList.find((w) => {
      if (!w) return false;
      return normalizeWebsiteUrl(w.urlSite || w.url) === normalizedTarget;
    }) || null
  );
};

const buildGenerateTempAgentUrl = () => {
  const params = new URLSearchParams();
  params.set('maxTokens', '4000');
  params.set('chatGPTModel', 'GPT_4_1');
  params.set('temperature', '0.3');
  params.set('reasoning', 'LOW');
  return `${GENERATE_TEMP_AGENT_ENDPOINT}?${params.toString()}`;
};

const getFileNameFromContentDisposition = (header = '') => {
  if (!header) return null;
  const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^["']|["']$/g, ''));
    } catch (e) {
      return utf8Match[1].trim().replace(/^["']|["']$/g, '');
    }
  }
  const plainMatch = /filename\s*=\s*([^;]+)/i.exec(header);
  if (plainMatch?.[1]) {
    return plainMatch[1].trim().replace(/^["']|["']$/g, '');
  }
  return null;
};

const getBrandVoiceFileNameFromBlob = (blob, headerFileName) => {
  if (headerFileName) return headerFileName;
  const type = String(blob?.type || '').toLowerCase();
  if (type.includes('pdf')) return 'brand-voice.pdf';
  if (type.includes('wordprocessingml')) return 'brand-voice.docx';
  if (type.includes('msword')) return 'brand-voice.doc';
  return 'brand-voice-file';
};

const fetchBrandVoiceFile = async (websiteId) => {
  if (!websiteId) return null;
  try {
    const fileRes = await axiosPrivate.get(getCompanyInfoFileUrl(websiteId), {
      responseType: 'blob',
    });
    const blob = fileRes?.data;
    if (!blob || blob.size === 0) return null;

    const type = String(blob.type || '').toLowerCase();
    if (type.includes('application/json') || type.includes('text/html')) return null;

    const headerFileName = getFileNameFromContentDisposition(
      fileRes.headers?.['content-disposition'] ||
        fileRes.headers?.['Content-Disposition']
    );
    const fileName = getBrandVoiceFileNameFromBlob(blob, headerFileName);
    return new File([blob], fileName, {
      type: blob.type || 'application/octet-stream',
    });
  } catch {
    return null;
  }
};

const buildGenerateTempAgentFormData = (payload, brandVoiceFile) => {
  const formDataToSend = new FormData();
  Object.entries(payload || {}).forEach(([key, value]) => {
    formDataToSend.append(key, value == null ? '' : String(value));
  });
  if (brandVoiceFile) {
    formDataToSend.append('brandVoiceFile', brandVoiceFile);
  }
  return formDataToSend;
};

const CONTENT_TYPE_LABEL_FALLBACK = {
  PRODUCT: 'Fiche produit',
  GMB: 'GMB',
  STORE_LOCATOR: 'Store locator',
  LONG_CATEGORY: 'Page catégorie',
  CONTENT_IMPROVEMENT: 'Enrichissement',
  OTHER: 'Autre',
};

const CONTENT_TYPE_TO_API_MAP = {
  'Fiche produit': 'PRODUCT',
  GMB: 'GMB',
  'Store locator': 'STORE_LOCATOR',
  'Page catégorie': 'LONG_CATEGORY',
};

function resolveContentTypeForApi(rawContentType, contentTypeList = []) {
  if (!rawContentType || typeof rawContentType !== 'string') return '';
  const rawValue = rawContentType.trim();
  if (!rawValue) return '';

  const normalizedRawValue = rawValue.toUpperCase().replace(/\s+/g, '_');
  const isKnownApiType = contentTypeList.some((item) => item?.type === normalizedRawValue);
  if (isKnownApiType) return normalizedRawValue;
  if (/^[A-Z_]{3,}$/.test(rawValue)) return rawValue;
  if (CONTENT_TYPE_TO_API_MAP[rawValue]) return CONTENT_TYPE_TO_API_MAP[rawValue];
  return rawValue;
}

/** Clés i18n (champ `thematic`) pour CustomChipsAutocomplete, alignées sur l’ancienne liste déroulante. */
const TONALITE_PRESET_LIST = [
  { id: 1, thematic: 'Expert' },
  { id: 11, thematic: 'Professionnel' },
  { id: 2, thematic: 'Technique' },
  { id: 3, thematic: 'Sérieux' },
  { id: 4, thematic: 'Amical' },
  { id: 5, thematic: 'Convivial' },
  { id: 6, thematic: 'Persuasif' },
  { id: 7, thematic: 'Dynamique' },
  { id: 8, thematic: 'Humoristique' },
  { id: 9, thematic: 'Rassurant' },
  { id: 10, thematic: 'Bienveillant' },
  { id: 12, thematic: 'Commercial' },
];

function tonaliteSelectionsToApiString(selections) {
  if (!Array.isArray(selections) || selections.length === 0) return '';
  return selections
    .map((item) => (item && typeof item === 'object' ? item.thematic : String(item || '').trim()))
    .filter(Boolean)
    .join(', ');
}

const extractAvailableCredits = (data, fallback = null) => {
  if (!data || typeof data !== 'object') return fallback;

  const directFields = ['remainingCredits', 'availableCredits', 'credits'];
  for (const field of directFields) {
    const value = Number(data[field]);
    if (Number.isFinite(value)) return Math.max(value, 0);
  }

  const totalCredits = Number(data.totalCredits);
  const usedCredits = Number(data.usedCredits);
  if (Number.isFinite(totalCredits) && Number.isFinite(usedCredits)) {
    return Math.max(totalCredits - usedCredits, 0);
  }

  return fallback;
};

function parseTonalityStringToSelections(raw, translateFn, presets) {
  if (raw == null || raw === '') return [];
  const str = String(raw).trim();
  if (!str) return [];

  const parts = str.split(',').map((p) => p.trim()).filter(Boolean);
  return parts.map((part) => {
    const byKey = presets.find(
      (p) =>
        p.thematic === part ||
        String(p.thematic).toLowerCase() === part.toLowerCase()
    );
    if (byKey) return { ...byKey };
    const byTranslated = presets.find((p) => translateFn(p.thematic) === part);
    if (byTranslated) return { ...byTranslated };
    return { id: 0, thematic: part };
  });
}

function CreateAgentIAFromList({
  returnToCreateProject,
  readOnly = false,
  listPath = '/AdminClient/AgentIAList',
  pagePath = ADMIN_CLIENT_CREATE_AGENT_IA_PAGE,
}) {
  const history = useHistory();
  const location = useLocation();
  const isReadOnly = readOnly;
  const agentListPath = listPath;
  usePageTracking(pagePath);

  const storedAgentId = localStorage.getItem('agentId');
  const agentId = storedAgentId;
  const isEditMode = Boolean(agentId);
  const [activeTab, setActiveTab] = useState('1');
  const activeTabRef = useRef(activeTab);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const storedDashboardWebsiteUrl =
    (typeof window !== 'undefined' &&
      (window.localStorage.getItem('AC_Dashboard_SelectedWebsite') ||
        window.sessionStorage.getItem('AC_Dashboard_SelectedWebsite'))) ||
    '';

  useEffect(() => {
    if (!storedAgentId) {
      localStorage.removeItem('agentId');
      localStorage.removeItem('agentMissionType');
      if (isReadOnly) {
        history.replace(agentListPath);
      }
    }
  }, [storedAgentId, isReadOnly, agentListPath, history]);

  const { t } = useTranslation();
  const [fileName, setFileName] = useState(t('Aucun fichier sélectionné'));
  const [knowledgeBaseFiles, setKnowledgeBaseFiles] = useState([]);
  const [knowledgeBaseFileError, setKnowledgeBaseFileError] = useState('');
  const [existingKnowledgeBaseNames, setExistingKnowledgeBaseNames] = useState([]);
  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [errorSuccessImg, setErrorSuccessImg] = useState(undefined);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingAgent, setIsGeneratingAgent] = useState(false);
  const [remainingCredits, setRemainingCredits] = useState(null);
  const [isInsufficientCreditsModalOpen, setIsInsufficientCreditsModalOpen] = useState(false);
  const [insufficientCreditsDisplay, setInsufficientCreditsDisplay] = useState(null);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);

  const [websiteList, setWebsiteList] = useState([]);
  const [availableLanguagesList, setAvailableLanguagesList] = useState([]);

  const [helpPanel, setHelpPanel] = useState({ open: false, field: null, contentType: '' });

  const [inlineEditsInProgress, setInlineEditsInProgress] = useState({});
  const [inlineEditsWarningOpen, setInlineEditsWarningOpen] = useState(false);
  const [inlineEditsWarningKeys, setInlineEditsWarningKeys] = useState([]);
  const inlineEditsFocusHandlersRef = useRef({});

  const inlineEditFieldOrder = ['structureTexte', 'RedacRules', 'recommandedLexique', 'instructions'];

  const openHelpPanel = (field, nextContentType = '') => setHelpPanel({ open: true, field, contentType: nextContentType });
  const closeHelpPanel = () => setHelpPanel({ open: false, field: null, contentType: '' });

  const handleFileChange = (e) => {
    const input = e.target;
    const files = Array.from(input?.files || []);
    if (input && typeof input.value === 'string') {
      input.value = '';
    }
    if (!files.length) return;

    const currentCount = existingKnowledgeBaseNames.length + knowledgeBaseFiles.length;
    const remaining = MAX_KNOWLEDGE_BASE_FILES - currentCount;

    if (remaining <= 0 || files.length > remaining) {
      setKnowledgeBaseFileError(t('Vous pouvez importer 3 fichiers maximum.'));
    } else {
      setKnowledgeBaseFileError('');
    }

    if (remaining <= 0) return;

    const acceptedFiles = files.slice(0, remaining);
    setKnowledgeBaseFiles((prev) => [...prev, ...acceptedFiles]);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    if (!e.dataTransfer?.files?.length) return;
    handleFileChange({ target: { files: e.dataTransfer.files } });
  };

  const handleRemoveFile = (indexToRemove) => {
    setKnowledgeBaseFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
    setKnowledgeBaseFileError('');
  };

  const handleRemoveExistingKbName = (indexToRemove) => {
    setExistingKnowledgeBaseNames((prev) => {
      const next = prev.filter((_, index) => index !== indexToRemove);
      if (next.length === 0) {
        setFileName(t('Aucun fichier sélectionné'));
      } else {
        setFileName(next.join(', '));
      }
      setKnowledgeBaseFileError('');
      return next;
    });
  };

  const knowledgeBaseFileCount = existingKnowledgeBaseNames.length + knowledgeBaseFiles.length;
  const isKnowledgeBaseLimitReached = knowledgeBaseFileCount >= MAX_KNOWLEDGE_BASE_FILES;

  const [contentTypeList, setContentTypeList] = useState([]);
  const [isOnlyOtherContentType, setIsOnlyOtherContentType] = useState(false);
  const [isTranslationEnabled, setIsTranslationEnabled] = useState(true);

  const roleList = [
    { name: t('Expert SEO') },
    { name: t('Expert en rédaction web') },
    { name: t('Expert en copywriting digital') },
    { name: t('Expert en content strategy') },
    { name: t('Expert en storytelling digital') },
    { name: t('Expert en marketing de contenu') },
    { name: t('Expert en social media / Community management') },
    { name: t('Expert UX writing / expérience utilisateur') },
    { name: t('Expert en création de contenu vidéo web') },
    { name: t('Expert en podcasting digital') },
    { name: t('Expert email marketing / newsletters') },
    { name: t('Expert en e-commerce / fiches produits') },
    { name: t('Expert en blogging') },
    { name: t('Expert en branding et image de marque') },
    { name: t('Expert en analyse de contenu et SEO technique') },
    { name: t('Expert pédagogique web') },
    { name: t('Expert en stratégie éditoriale digitale') },
  ];

  const missionFromStorage = localStorage.getItem('agentMissionType') || t('Rédaction');

  const [formData, setFormData] = useState({
    nomAgent: '',
    objectif: '',
    instructions: '',
    tonalite: [],
    cible: '',
    structureText: '',
    RedacRules: '',
    structureTexte: '',
    InterditExpressions: '',
    TypeProducts: '',
    recommandedLexique: '',
    urls: '',
    contentType: '',
    langue: null,
    selectedWebsite: null,
    websiteId: '',
    websiteUrl: storedDashboardWebsiteUrl || '',
    mission: missionFromStorage,
  });

  // Guide interactif sur la page (bouton "?")
  const [runCreateAgentiaTour, setRunCreateAgentiaTour] = useState(false);
  const [createAgentiaTourSteps, setCreateAgentiaTourSteps] = useState([]);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [showStepGuideBadge, setShowStepGuideBadge] = useState(true);

  const tabByStepIndex = [
    '1', '1', '1', '1',
    '2', '2', '2',
    '3', '3', '3', '3', '3',
    '4', '4',
  ];

  const buildCreateAgentiaTourSteps = () => [
    {
      target: "#agentia-create-step1-mission",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Mission de l'agent")}</h5>
          <p>
            {t("Choisissez la mission de votre agent.")} <br />
            {t("👉 Rédaction : pour créer du contenu original")} <br />
            {t("👉 Traduction : pour adapter un contenu existant dans une autre langue")}
          </p>
          <p>{t("Ce choix détermine le fonctionnement de l’agent et les options disponibles par la suite.")}</p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
    },
    {
      target: "#agentia-create-step2-nom-agent",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Nom de l’agent")}</h5>
          <p>{t("Donnez un nom à votre agent comme s’il faisait partie de votre équipe.")}</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
    },
    {
      target: "#agentia-create-step3-role",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Rôle")}</h5>
          <p>
            {t("Précisez le rôle de votre agent (rédacteur SEO, copywriter, expert métier…).")} <br />
            {t("Ce rôle détermine sa posture, sa manière de rédiger (expert, pédagogue, commercial…).")} <br />
            {t("Et influence directement la manière dont il rédige, structure et priorise l’information.")}
          </p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
    },
    {
      target: "#agentia-create-step4-type-contenu",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Type de contenu")}</h5>
          <p>
            {t("Sélectionnez parmi la liste le type de contenu que l’agent doit produire (Page catégorie, fiche produit, GMB, Store locator ou ajouter d’autre types comme article de blog, etc …).")} <br />
            {t("Cela permet d’adapter automatiquement la structure, le style et les attentes SEO.")}
          </p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
    },
    {
      target: "#agentia-create-step5-tonalite",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Tonalité")}</h5>
          <p>
            {t("Choisissez ou personnalisez la tonalité de vos contenus : professionnelle, pédagogique, engageante, premium, etc.")} <br />
            {t("La tonalité garantit une cohérence avec votre image de marque et votre positionnement.")}
          </p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
    },
    {
      target: "#agentia-create-step6-cible",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Cible / Audience")}</h5>
          <p>{t("Définissez précisément à qui s’adresse votre contenu.")}</p><br />
          <p>{t("Plus votre audience est claire (ex : débutants, experts, e-commerçants, parents…), plus le contenu sera pertinent, adapté et efficace.")}</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
    },
    {
      target: "#agentia-create-step7-type-produit-service",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Type de produit / service")}</h5>
          <p>{t("Définissez la nature de produit ou le service visé par la rédaction")}</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
    },
    {
      target: "#agentia-create-guide-textarea-structureTexte",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Structure du contenu")}</h5>
          <p>
            {t("Définissez la structure type que l’agent doit suivre pour générer votre contenu.")} <br />
            {t("Indiquez les titres (H1, H2, H3…) et les éléments clés à inclure. Exemple : nom du produit, bénéfices, description, arguments, FAQ…")} <br />
            {t("Une structure claire permet d’obtenir un contenu mieux organisé, plus lisible et optimisé pour le SEO.")}
          </p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
    },
    {
      target: "#agentia-create-guide-textarea-RedacRules",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Règles rédactionnelles")}</h5>
          <p>{t("Ajoutez des règles spécifiques de rédaction : style, vocabulaire, la manière de s’adresser au lecteur et le rythme du contenu.")}</p>
          <p>{t("Ces consignes permettent d’obtenir des contenus homogènes et conformes à vos standards éditoriaux.")}</p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
    },
    {
      target: "#agentia-create-step8-recommanded-lexique",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Lexiques recommandés")}</h5>
          <p>{t("Indiquez les mots et expressions que l’agent doit privilégier dans le contenu. Cela permet d’enrichir le texte, d’améliorer le SEO et de renforcer votre univers sémantique.")}</p>
          <p>{t("Exemple : innovation, performance, naturel, expertise…")}</p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
    },
    {
      target: "#agentia-create-step9-interdit-expressions",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Expression interdites")}</h5>
          <p>{t("Listez les mots ou formulations à éviter. Utile pour protéger votre image de marque et éviter les contenus inadaptés.")}</p>
          <p>{t("Exemple : termes trop promotionnels, jargon technique, expressions négativ")}</p>
        </div>
      ),
      placement: "top",
      disableBeacon: true,
    },
    {
      target: "#agentia-create-guide-textarea-instructions",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Consignes complémentaires")}</h5>
          <p>{t("Ajoutez des instructions spécifiques pour affiner le contenu généré. Idéal pour préciser un angle, des éléments à mettre en avant ou des contraintes particulières.")}</p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
    },
    {
      target: "#agentia-create-step11-kb-local",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Base de connaissance locale")}</h5>
          <p>{t("Ajoutez vos ressources internes (documents, briefs, guidelines, contenus existants…).")}</p>
          <p>{t("L’agent s’appuie sur ces éléments pour produire des contenus plus précis, cohérents et alignés avec votre expertise.")}</p>
        </div>
      ),
      placement: "top",
      disableBeacon: true,
    },
    {
      target: "#agentia-create-step12-kb-web",
      content: (
        <div>
          <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Base de connaissance web")}</h5>
          <p>{t("Ajoutez des URLs (site, blog, concurrents…) pour guider l’agent. Il peut analyser leur structure, leur contenu et s’en inspirer pour produire des textes plus performants et compétitifs.")}</p>
        </div>
      ),
      placement: "top",
      disableBeacon: true,
    },
  ];

  const handleCreateAgentiaTourCallback = (data) => {
    const { status, action, type, index } = data;

    if (action === ACTIONS.CLOSE || action === ACTIONS.SKIP) {
      setRunCreateAgentiaTour(false);
      setTourStepIndex(0);
      return;
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunCreateAgentiaTour(false);
      setTourStepIndex(0);
      return;
    }

    if (type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT) {
      const nextIndex = index + 1;
      const requiredTab = tabByStepIndex[nextIndex];

      if (requiredTab && requiredTab !== activeTabRef.current) {
        setTourStepIndex(nextIndex);
        setRunCreateAgentiaTour(false);
        setActiveTab(requiredTab);
        setTimeout(() => {
          setCreateAgentiaTourSteps(buildCreateAgentiaTourSteps());
          setRunCreateAgentiaTour(true);
        }, 350);
      } else {
        setTourStepIndex(nextIndex);
      }
    }

    if (type === EVENTS.STEP_AFTER && action === ACTIONS.PREV) {
      const prevIndex = index - 1;
      const requiredTab = tabByStepIndex[prevIndex];

      if (requiredTab && requiredTab !== activeTabRef.current) {
        setTourStepIndex(prevIndex);
        setRunCreateAgentiaTour(false);
        setActiveTab(requiredTab);
        setTimeout(() => {
          setCreateAgentiaTourSteps(buildCreateAgentiaTourSteps());
          setRunCreateAgentiaTour(true);
        }, 350);
      } else {
        setTourStepIndex(prevIndex);
      }
    }
  };

  const startCreateAgentiaTour = () => {
    setFormData((prev) => ({ ...prev, mission: t("Rédaction") }));
    try {
      localStorage.setItem("agentMissionType", t("Rédaction"));
    } catch (e) { }

    setActiveTab('1');
    setCreateAgentiaTourSteps(buildCreateAgentiaTourSteps());
    setRunCreateAgentiaTour(false);
    setTimeout(() => setRunCreateAgentiaTour(true), 0);
  };

  const tabStartStepIndexMap = {
    '1': 0,
    '2': 4,
    '3': 7,
    '4': 12,
  };

  const startCreateAgentiaTourFromTab = (tabId) => {
    const targetTab = tabId || activeTab;
    const steps = buildCreateAgentiaTourSteps();
    const startIndex = tabStartStepIndexMap[targetTab] ?? 0;

    setActiveTab(targetTab);
    setCreateAgentiaTourSteps(steps);
    setTourStepIndex(startIndex);
    setRunCreateAgentiaTour(false);
    setTimeout(() => setRunCreateAgentiaTour(true), 0);
  };

  const isGeneralStepValid =
    !!formData.nomAgent?.trim() &&
    !!formData.selectedWebsite &&
    (formData.mission === t('Traduction') ||
      (!!formData.objectif?.trim() && !!formData.contentType?.trim()));

  const isTonAudienceStepValid =
    formData.mission === t('Traduction')
      ? !!formData.langue
      : !!formData.langue &&
      Array.isArray(formData.tonalite) &&
      formData.tonalite.length > 0 &&
      !!formData.cible?.trim() &&
      !!formData.TypeProducts?.trim();

  const [agentData, setAgentData] = useState(null);
  useEffect(() => {
    document.title = isReadOnly ? "Typix - Détail agent IA" : "Typix - Créer un agent IA";
  }, [isReadOnly]);

  useEffect(() => {
    Promise.all([
      axiosPrivate.get('/project/website/all').then((res) => res.data || []).catch(() => []),
      axiosPrivate.get('/project/language/all').then((res) => res.data || []).catch(() => []),
      axiosPrivate.get('/project/company-quotas').then((res) => res.data || {}).catch(() => ({})),
    ]).then(([websites, languages, quotas]) => {
      setWebsiteList(websites);
      setAvailableLanguagesList(languages);
      setRemainingCredits(extractAvailableCredits(quotas));
      const translationEnabledRaw = quotas?.translationEnabled;
      const nextIsTranslationEnabled = translationEnabledRaw === undefined
        ? true
        : String(translationEnabledRaw).toLowerCase() !== 'false';
      setIsTranslationEnabled(nextIsTranslationEnabled);

      const rawTypes =
        (Array.isArray(quotas?.availableContentTypes) && quotas.availableContentTypes) ||
        (Array.isArray(quotas?.enabledContentTypes) && quotas.enabledContentTypes) ||
        (Array.isArray(quotas?.contentTypes) && quotas.contentTypes) ||
        [];

      const normalizeType = (value) =>
        String(value || '')
          .trim()
          .toUpperCase()
          .replace(/\s+/g, '_');

      const uniqueTypes = Array.from(
        new Set(
          rawTypes
            .map((item) => normalizeType(typeof item === 'string' ? item : (item?.type || item?.id || item?.name || '')))
            .filter(Boolean)
        )
      );

      const resolvedTypes = uniqueTypes.length > 0
        ? uniqueTypes
        : ['PRODUCT', 'GMB', 'STORE_LOCATOR', 'LONG_CATEGORY'];

      const options = resolvedTypes
        .filter((type) => type !== 'OTHER')
        .map((type) => ({
          type,
          name: t(CONTENT_TYPE_LABEL_FALLBACK[type] || type),
        }));

      setContentTypeList(options);
      setIsOnlyOtherContentType(resolvedTypes.length === 1 && resolvedTypes[0] === 'OTHER');
    });
  }, [t]);

  useEffect(() => {
    if (isEditMode && agentId) {
      setIsLoading(true);
      axiosPrivate
        .get(`/smart-content/agent_ia/${agentId}`)
        .then((res) => setAgentData(res.data))
        .catch((error) => {
          console.error('Erreur lors du chargement de l\'agent:', error);
          setErrorSuccessMsg('Erreur lors du chargement des données de l\'agent');
          setIsSuccess(false);
          setErrorSuccess(true);
          setIsLoading(false);
        });
    }
  }, [isEditMode, agentId]);

  useEffect(() => {
    if (!agentData || !isEditMode) return;

    const agent = agentData;
    const rawContentType = (agent.typeContenu || agent.contentType || '').toString().trim();
    const normalizedContentType = rawContentType
      .toUpperCase()
      .replace(/\s+/g, '_');
    const contentTypeDisplay =
      t(CONTENT_TYPE_LABEL_FALLBACK[normalizedContentType] || '') || rawContentType;

    let selectedLangue = null;
    if (agent.langue && availableLanguagesList.length > 0) {
      const agentLangueCandidates = getAgentLangueCandidates(agent.langue).map(normalizeText).filter(Boolean);
      selectedLangue = availableLanguagesList.find(lang => {
        if (!lang) return false;
        const langCandidates = [
          lang.language,
          lang.name,
          lang.code,
          lang.id,
          // Certaines sources utilisent des libellés traduits.
          t(lang.language || ''),
        ];
        const langCandidatesNorm = langCandidates.map(normalizeText).filter(Boolean);
        return agentLangueCandidates.some((agentCandidate) => langCandidatesNorm.includes(agentCandidate));
      }) || null;
    }

    const selectedWebsiteFromDashboard = findWebsiteByUrl(
      websiteList,
      storedDashboardWebsiteUrl
    );

    let urlsValue = '';
    if (agent.urls) {
      if (Array.isArray(agent.urls)) {
        urlsValue = agent.urls.filter(url => url && String(url).trim()).join(', ');
      } else if (typeof agent.urls === 'string') {
        urlsValue = agent.urls.trim();
      }
    }

    setFormData({
      nomAgent: agent.name || '',
      objectif: agent.role || '',
      instructions: agent.instructions || '',
      tonalite: parseTonalityStringToSelections(agent.tonality, t, TONALITE_PRESET_LIST),
      cible: agent.audience || '',
      structureText: '',
      RedacRules: agent.redactionRules || '',
      structureTexte: agent.resultStructure || '',
      InterditExpressions: agent.bannedExpressions || '',
      TypeProducts: agent.productType || '',
      recommandedLexique: agent.recommendedLexicons || '',
      urls: urlsValue,
      contentType: contentTypeDisplay,
      langue: selectedLangue,
      selectedWebsite: selectedWebsiteFromDashboard,
      websiteId: selectedWebsiteFromDashboard?.id || '',
      websiteUrl: storedDashboardWebsiteUrl || selectedWebsiteFromDashboard?.urlSite || selectedWebsiteFromDashboard?.url || '',
      mission: missionFromStorage,
    });

    if (agent.knowledgeBase) {
      let kbArray = [];
      if (typeof agent.knowledgeBase === 'string') {
        kbArray = agent.knowledgeBase
          .split(',')
          .map((v) => (v || '').toString().trim())
          .filter((v) => v.length > 0);
      } else if (Array.isArray(agent.knowledgeBase)) {
        kbArray = agent.knowledgeBase
          .map((v) => (v || '').toString().trim())
          .filter((v) => v.length > 0);
      }
      if (kbArray.length > 0) {
        setExistingKnowledgeBaseNames(kbArray);
        setFileName(kbArray.join(', '));
      }
    } else {
      setExistingKnowledgeBaseNames([]);
      setFileName(t('Aucun fichier sélectionné'));
    }
    setIsLoading(false);
  }, [agentData, websiteList, availableLanguagesList, isEditMode, missionFromStorage, storedDashboardWebsiteUrl, t]);

  useEffect(() => {
    if (websiteList.length > 0 && !formData.selectedWebsite && storedDashboardWebsiteUrl) {
      const website = findWebsiteByUrl(websiteList, storedDashboardWebsiteUrl);
      if (website) {
        setFormData(prev => ({
          ...prev,
          selectedWebsite: website,
          websiteId: website?.id || '',
          websiteUrl: website?.urlSite || website?.url || storedDashboardWebsiteUrl,
        }));
      }
    }
  }, [websiteList, storedDashboardWebsiteUrl, formData.selectedWebsite]);

  useEffect(() => {
    if (agentData && agentData.langue && availableLanguagesList.length > 0 && !formData.langue) {
      const agentLangueCandidates = getAgentLangueCandidates(agentData.langue).map(normalizeText).filter(Boolean);
      const selectedLangue = availableLanguagesList.find(lang => {
        if (!lang) return false;
        const langCandidates = [
          lang.language,
          lang.name,
          lang.code,
          lang.id,
          // Certaines sources utilisent des libellés traduits.
          t(lang.language || ''),
        ];
        const langCandidatesNorm = langCandidates.map(normalizeText).filter(Boolean);
        return agentLangueCandidates.some((agentCandidate) => langCandidatesNorm.includes(agentCandidate));
      });
      if (selectedLangue) {
        setFormData(prev => ({ ...prev, langue: selectedLangue }));
      }
    }
  }, [agentData, availableLanguagesList, formData.langue]);

  // Pré-remplissage de la langue depuis le site web (et non depuis la langue de l'agent).
  useEffect(() => {
    const websiteLang = formData?.selectedWebsite?.language;
    if (!websiteLang || availableLanguagesList.length <= 0) return;

    // 1) Match par ID (si disponible)
    const websiteLangId = websiteLang?.id;
    if (websiteLangId != null) {
      const matchedLanguage = availableLanguagesList.find(
        (lang) => lang?.id != null && String(lang.id) === String(websiteLangId)
      );
      if (matchedLanguage) {
        setFormData((prev) => ({ ...prev, langue: matchedLanguage }));
        return;
      }
    }

    // 2) Fallback match par libellés (si pas d'ID ou structure différente)
    const websiteLangCandidates = (() => {
      if (typeof websiteLang === 'string' || typeof websiteLang === 'number' || typeof websiteLang === 'boolean') {
        return [String(websiteLang)];
      }
      if (typeof websiteLang === 'object') {
        return [
          websiteLang.language,
          websiteLang.name,
          websiteLang.code,
          websiteLang.langue,
        ].filter((v) => v != null);
      }
      return [];
    })().map(normalizeText).filter(Boolean);

    if (websiteLangCandidates.length <= 0) return;

    const matchedLanguage = availableLanguagesList.find((lang) => {
      const langCandidates = [
        lang?.language,
        lang?.name,
        lang?.code,
        lang?.id,
        // Permet de matcher si une source renvoie déjà la valeur traduite.
        t(lang?.language || ''),
      ].filter((v) => v != null);
      const langCandidatesNorm = langCandidates.map(normalizeText).filter(Boolean);
      return websiteLangCandidates.some((candidate) => langCandidatesNorm.includes(candidate));
    });

    if (!matchedLanguage) return;
    setFormData((prev) => ({ ...prev, langue: matchedLanguage }));
  }, [formData.selectedWebsite, availableLanguagesList]);

  useEffect(() => {
    if (!isLoading && isEditMode) {
      requestAnimationFrame(() => {
        document.querySelectorAll('.auto-resize-textarea').forEach(el => {
          el.style.height = 'auto';
          el.style.height = el.scrollHeight + 'px';
        });
      });
    }
  }, [isLoading, isEditMode, formData.RedacRules, formData.recommandedLexique, formData.InterditExpressions, formData.structureTexte, formData.instructions, formData.objectif, formData.urls]);

  useEffect(() => {
    requestAnimationFrame(() => {
      document.querySelectorAll('.auto-resize-textarea').forEach(el => {
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
      });
    });
  }, [activeTab]);

  useEffect(() => {
    if (isTranslationEnabled) return;
    if (formData.mission !== t('Traduction')) return;
    setFormData((prev) => ({ ...prev, mission: t('Rédaction') }));
    try {
      localStorage.setItem('agentMissionType', t('Rédaction'));
    } catch { }
  }, [isTranslationEnabled, formData.mission, t]);

  const handleInlineEditEditingChange = (fieldKey, fieldLabel, isEditing) => {
    if (!fieldKey) return;
    setInlineEditsInProgress((prev) => {
      const next = { ...prev };
      if (isEditing) next[fieldKey] = fieldLabel || fieldKey;
      else delete next[fieldKey];
      return next;
    });
  };

  const registerInlineEditFocusHandler = (fieldKey, focusHandler) => {
    if (!fieldKey || typeof focusHandler !== 'function') return;
    inlineEditsFocusHandlersRef.current[fieldKey] = focusHandler;
  };

  const openInlineEditsWarningIfNeeded = () => {
    const pendingKeys = inlineEditFieldOrder.filter((k) => inlineEditsInProgress[k]);
    if (!pendingKeys.length) return false;
    setInlineEditsWarningKeys(pendingKeys);
    setInlineEditsWarningOpen(true);
    return true;
  };

  const handleSubmitWithInlineEditsCheck = () => {
    const shouldBlock = openInlineEditsWarningIfNeeded();
    if (shouldBlock) return;
    handleSubmit();
  };

  const handleSubmit = () => {
    const formDataFORM = new FormData();

    if (!formData.nomAgent?.trim()) {
      setIsSuccess(false);
      setErrorSuccessMsg(t('Le nom de l\'agent est requis'));
      setErrorSuccess(true);
      return;
    }

    if (!formData.langue?.language) {
      setIsSuccess(false);
      setErrorSuccessMsg(t('La langue est requise'));
      setErrorSuccess(true);
      return;
    }

    const websiteUrlToSend = formData?.selectedWebsite?.urlSite || formData?.selectedWebsite?.url || '';
    if (!websiteUrlToSend) {
      setIsSuccess(false);
      setErrorSuccessMsg(t('Le site web est requis'));
      setErrorSuccess(true);
      return;
    }

    formDataFORM.append('name', formData.nomAgent.trim());
    formDataFORM.append('instructions', formData.instructions.trim());
    formDataFORM.append('langue', formData.langue.language);
    formDataFORM.append('websiteUrl', String(websiteUrlToSend));

    if (formData.mission === t('Traduction')) {
      // Traduction spécifique
    } else {
      formDataFORM.append('role', formData.objectif || '');
      formDataFORM.append('tonality', tonaliteSelectionsToApiString(formData.tonalite));
      formDataFORM.append('audience', formData.cible || '');
      formDataFORM.append('productType', formData.TypeProducts || '');
      formDataFORM.append('redactionRules', formData.RedacRules || '');
      formDataFORM.append('resultStructure', formData.structureTexte || '');
      formDataFORM.append('recommendedLexicons', formData.recommandedLexique || '');
      formDataFORM.append('bannedExpressions', formData.InterditExpressions || '');
      formDataFORM.append('personalityTraits', '');

      const typeContenuToSend = resolveContentTypeForApi(formData.contentType, contentTypeList);

      formDataFORM.append('typeContenu', typeContenuToSend);
    }

    if (knowledgeBaseFiles.length > 0) {
      knowledgeBaseFiles.forEach((file) => {
        formDataFORM.append('knowledgeBaseFiles', file);
      });
    }

    if (isEditMode && existingKnowledgeBaseNames.length > 0) {
      existingKnowledgeBaseNames.forEach((name) => {
        formDataFORM.append('knowledgeBase', name);
      });
    }

    if (formData.urls && formData.urls.trim()) {
      formDataFORM.append('urls', formData.urls.trim());
    }



    for (let [key, value] of formDataFORM.entries()) {
      console.log(`${key}:`, value);
    }

    const endpoint = isEditMode
      ? formData.mission === t('Traduction')
        ? `/smart-content/agent_ia/traduction/${agentId}/update`
        : `/smart-content/agent_ia/redaction/${agentId}/update`
      : formData.mission === t('Traduction')
        ? '/smart-content/agent_ia/traduction/create'
        : '/smart-content/agent_ia/redaction/create';

    const request = isEditMode
      ? axiosPrivate.put(endpoint, formDataFORM, {
        headers: { 'Content-Type': 'multipart/form-data', 'X-OpenAI-Api-Key': '' },
      })
      : axiosPrivate.post(endpoint, formDataFORM, {
        headers: { 'Content-Type': 'multipart/form-data', 'X-OpenAI-Api-Key': '' },
      });

    setIsSubmitting(true);
    request
      .then(() => {
        setIsSuccess(true);
        setErrorSuccessMsg(isEditMode ? t('Agent IA mis à jour avec succès') : t('Agent IA créé avec succès'));
        setErrorSuccess(true);
        const fromContentGeneration =
          sessionStorage.getItem('isNavigatingToAgentEdit') === 'true';
        if (fromContentGeneration && isEditMode) {
          return;
        }
        try {
          localStorage.removeItem('agentMissionType');
          localStorage.removeItem('agentId');
        } catch { }
        setTimeout(() => history.push(agentListPath), 2000);
      })
      .catch((error) => {
        console.error('Erreur lors de la soumission:', error);
        setIsSuccess(false);
        setErrorSuccessMsg(isEditMode ? t('Erreur lors de la mise à jour de l\'agent') : t('Erreur lors de la création de l\'agent'));
        setErrorSuccess(true);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const resolveWebsiteId = () => {
    const directId = formData?.selectedWebsite?.id || formData?.websiteId;
    if (directId) return directId;

    const websiteUrl = String(
      formData?.selectedWebsite?.urlSite ||
        formData?.selectedWebsite?.url ||
        formData?.websiteUrl ||
        ''
    )
      .trim()
      .toLowerCase();
    if (!websiteUrl) return null;

    return findWebsiteByUrl(websiteList, websiteUrl)?.id || null;
  };

  const buildGenerateAgentPayload = () => {
    const website =
      formData?.selectedWebsite?.urlSite ||
      formData?.selectedWebsite?.url ||
      formData?.websiteUrl ||
      '';

    return {
      website: String(website).trim(),
      role: String(formData.objectif || '').trim(),
      service: formatWebsiteThematicsService(formData.selectedWebsite),
      contentType: resolveContentTypeForApi(formData.contentType, contentTypeList),
      langue: String(formData.langue?.language || '').trim(),
    };
  };

  const applyGeneratedAgentData = (agent) => {
    if (!agent || typeof agent !== 'object') return;

    setFormData((prev) => ({
      ...prev,
      ...(agent.name ? { nomAgent: agent.name } : {}),
      ...(agent.role ? { objectif: agent.role } : {}),
      ...(agent.instructions != null ? { instructions: agent.instructions } : {}),
      ...(agent.tonality != null
        ? { tonalite: parseTonalityStringToSelections(agent.tonality, t, TONALITE_PRESET_LIST) }
        : {}),
      ...(agent.audience ? { cible: agent.audience } : {}),
      ...(agent.productType ? { TypeProducts: agent.productType } : {}),
      ...(agent.redactionRules != null ? { RedacRules: agent.redactionRules } : {}),
      ...(agent.resultStructure != null ? { structureTexte: agent.resultStructure } : {}),
      ...(agent.recommendedLexicons != null ? { recommandedLexique: agent.recommendedLexicons } : {}),
      ...(agent.bannedExpressions != null ? { InterditExpressions: agent.bannedExpressions } : {}),
    }));
  };

  const handleGenerateAgent = async () => {
    const payload = buildGenerateAgentPayload();

    if (!payload.website) {
      setIsSuccess(false);
      setErrorSuccessMsg(t('Le site web est requis'));
      setErrorSuccess(true);
      return;
    }

    if (!payload.role) {
      setIsSuccess(false);
      setErrorSuccessMsg(t('Le rôle est requis'));
      setErrorSuccess(true);
      return;
    }

    if (!payload.contentType) {
      setIsSuccess(false);
      setErrorSuccessMsg(t('Le type de contenu est requis'));
      setErrorSuccess(true);
      return;
    }

    if (!payload.langue) {
      setIsSuccess(false);
      setErrorSuccessMsg(t('La langue est requise'));
      setErrorSuccess(true);
      return;
    }

    setIsGeneratingAgent(true);
    let freshRemainingCredits = remainingCredits;
    try {

      try {
        const quotasRes = await axiosPrivate.get('/project/company-quotas');
        freshRemainingCredits = extractAvailableCredits(quotasRes?.data || {}, remainingCredits);
        setRemainingCredits(freshRemainingCredits);
      } catch (quotaError) {
  
        console.warn('Erreur lors du rafraîchissement de la quota au clic:', quotaError);
      }

      const brandVoiceFile = await fetchBrandVoiceFile(resolveWebsiteId());
      const { data } = await axiosPrivate.post(
        buildGenerateTempAgentUrl(),
        buildGenerateTempAgentFormData(payload, brandVoiceFile),
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      applyGeneratedAgentData(data);
      setIsSuccess(true);
      setErrorSuccessMsg(t('Agent généré avec succès'));
      setErrorSuccess(true);
    } catch (error) {
      console.error('Erreur lors de la génération de l\'agent:', error);

      if (error?.response?.status === 402) {
        const availableCredits = extractAvailableCredits(
          error?.response?.data,
          freshRemainingCredits
        );
        setInsufficientCreditsDisplay(availableCredits);
        setIsInsufficientCreditsModalOpen(true);
        return;
      }

      setIsSuccess(false);
      setErrorSuccessMsg(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('Erreur lors de la génération de l\'agent')
      );
      setErrorSuccess(true);
    } finally {
      setIsGeneratingAgent(false);
    }
  };

  const handleCancel = () => {
    const confirmCancel = window.confirm(
      isEditMode ? t('Annuler les modifications ?') : t('Annuler la création de l\'agent ?')
    );
    if (!confirmCancel) return;

    localStorage.removeItem('agentMissionType');
    localStorage.removeItem('agentId');

    setFormData({
      nomAgent: '', objectif: '', instructions: '', tonalite: [], cible: '',
      structureText: '', RedacRules: '', structureTexte: '', InterditExpressions: '',
      recommandedLexique: '', TypeProducts: '', urls: '',
      contentType: '', langue: null,
    });

    if (typeof returnToCreateProject === 'function') {
      returnToCreateProject();
    } else if (agentListPath.startsWith('/Consultant')) {
      history.push(agentListPath);
    } else {
      history.push('/AdminClient/createIAsmarttext');
    }
  };

  const structurePlaceholder = getStructurePlaceholderByContentType();

  const handleChange = (field, value) => {
    if (field === 'contentType') {
      console.log('Selected contentType value:', value);
    }
    setFormData({ ...formData, [field]: value });
  };

  const goBack = () => {
    if (typeof returnToCreateProject === 'function') returnToCreateProject();
    else history.goBack();
  };

  useEffect(() => {
    setShowStepGuideBadge(true);
  }, [activeTab]);

  const containerClass = !returnToCreateProject ? 'box-wrapper p-4' : 'p-4';

  if (isLoading) {
    return (
      <div className={containerClass}>
        <div className="text-center p-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('Chargement')}...</span>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: '1', label: t('Informations principales') },
    { id: '2', label: t('Ton & audience') },
    { id: '3', label: t('Structure & rédaction') },
    { id: '4', label: t('Base de connaissances') },
  ];

  const currentStepNumber = Number(activeTab) || 1;
  const currentStepLabel = tabs.find((tab) => tab.id === activeTab)?.label || tabs[0].label;

  const stepGuideTextByTab = {
    '1': t("Donnez un nom à votre agent et définissez sa mission. Ces informations permettent à l'IA d'adapter précisément le contenu généré à vos besoins."),
    '2': t("Définissez le ton éditorial (formel, conversationnel, expert) et la cible. L'audience détermine le registre et la profondeur du contenu."),
    '3': t("Configurez la structure type de vos contenus : longueur, niveaux de titres, sections incontournables, balises SEO."),
    '4': t("Enrichissez l'agent avec vos ressources : URLs, documents, mots-clés, briefs — pour des contenus ultra-personnalisés."),
  };

  function normalizeText(value) {
    return String(value ?? '').trim().toLowerCase();
  }


  function getAgentLangueCandidates(agentLangue) {
    if (agentLangue == null) return [];
    if (
      typeof agentLangue === 'string' ||
      typeof agentLangue === 'number' ||
      typeof agentLangue === 'boolean'
    ) {
      return [String(agentLangue)];
    }
    if (typeof agentLangue === 'object') {
      const candidates = [];
      ['language', 'name', 'code', 'id', 'langue'].forEach((k) => {
        if (agentLangue?.[k] != null) candidates.push(String(agentLangue[k]));
      });
      // Si jamais l'objet contient une valeur directement "value"
      if (agentLangue?.value != null) candidates.push(String(agentLangue.value));
      return candidates;
    }
    return [String(agentLangue)];
  }

  const fromBase64 = (b64, mime = 'image/png') => `data:${mime};base64,${b64}`;
  const fromBytes = (bytes, mime = 'image/png') => {
    try {
      const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
      const blob = new Blob([u8], { type: mime });
      return URL.createObjectURL(blob);
    } catch { return null; }
  };

  const inferAgentImageUrl = (agent) => {
    if (!agent) return null;
    const mime = agent.mimeType || 'image/png';
    if (agent.imageBase64) return fromBase64(agent.imageBase64, mime);
    if (agent.image && typeof agent.image === 'string' && /^[A-Za-z0-9+/=]+$/.test(agent.image)) return fromBase64(agent.image, mime);
    if (agent.imageBytes || Array.isArray(agent.image)) return fromBytes(agent.imageBytes || agent.image, mime);
    return agent.imageUrl || null;
  };

  const agentImageUrl = inferAgentImageUrl(agentData) || 'Images/agentIA.png';
  const missionLabel = formData.mission === t('Traduction') ? t('Traduction') : t('Rédaction');
  const missionBadgeClass = missionLabel === t('Traduction') ? 'badge-blue' : 'badge-green';
  const displayedContentTypeLabel = t(
    CONTENT_TYPE_LABEL_FALLBACK[
    String(formData.contentType || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_')
    ] || formData.contentType || ''
  );

  const hasNomAgent = !!formData.nomAgent?.trim();
  const hasWebsite = !!(formData.selectedWebsite?.urlSite || formData.selectedWebsite?.url);
  const hasLangue = !!formData.langue;
  const isRedactionMission = missionLabel === t('Rédaction');
  const hasRedactionFields = isRedactionMission
    ? !!(
      formData.objectif?.trim() &&
      formData.contentType?.trim() &&
      Array.isArray(formData.tonalite) &&
      formData.tonalite.length > 0 &&
      formData.cible?.trim() &&
      formData.TypeProducts?.trim()
    )
    : true;
  const isFormValid = hasNomAgent && hasWebsite && hasLangue && hasRedactionFields;

  const canGenerateAgent =
    isRedactionMission &&
    hasWebsite &&
    !!formData.objectif?.trim() &&
    !!formData.contentType?.trim() &&
    !!formData.langue?.language;

  const hasGeneralFieldsFilled =
    !!formData.nomAgent?.trim() ||
    !!formData.selectedWebsite ||
    !!formData.objectif?.trim() ||
    !!formData.contentType?.trim();

  const hasTonAudienceFieldsFilled =
    !!formData.langue ||
    (Array.isArray(formData.tonalite) && formData.tonalite.length > 0) ||
    !!formData.cible?.trim() ||
    !!formData.TypeProducts?.trim();

  const structureFields = [
    formData.structureTexte,
    formData.RedacRules,
    formData.recommandedLexique,
    formData.InterditExpressions,
  ];
  const filledStructureCount = structureFields.filter((v) => v && v.trim()).length;
  const isStructureSectionValid = filledStructureCount === structureFields.length && filledStructureCount > 0;
  const hasStructureSectionStarted = filledStructureCount > 0;

  const hasKnowledgeSectionFilled =
    (knowledgeBaseFiles && knowledgeBaseFiles.length > 0) ||
    !!formData.urls?.trim();

  const step1Total = isRedactionMission ? 4 : 2;
  const step1Filled =
    (formData.nomAgent?.trim() ? 1 : 0) +
    (formData.selectedWebsite ? 1 : 0) +
    (isRedactionMission ? (formData.objectif?.trim() ? 1 : 0) : 0) +
    (isRedactionMission ? (formData.contentType?.trim() ? 1 : 0) : 0);

  const step2Total = isRedactionMission ? 4 : 1;
  const step2Filled =
    (formData.langue ? 1 : 0) +
    (isRedactionMission ? (Array.isArray(formData.tonalite) && formData.tonalite.length > 0 ? 1 : 0) : 0) +
    (isRedactionMission ? (formData.cible?.trim() ? 1 : 0) : 0) +
    (isRedactionMission ? (formData.TypeProducts?.trim() ? 1 : 0) : 0);

  const step3Total = isRedactionMission ? 4 : 1;
  const step3Filled = isRedactionMission
    ? filledStructureCount
    : (formData.instructions?.trim() ? 1 : 0);

  const step4Total = 2;
  const step4Filled =
    ((knowledgeBaseFiles && knowledgeBaseFiles.length > 0) || (existingKnowledgeBaseNames && existingKnowledgeBaseNames.length > 0) ? 1 : 0) +
    (formData.urls?.trim() ? 1 : 0);

  const tabProgressMap = {
    '1': step1Total > 0 ? Math.min(1, step1Filled / step1Total) : 0,
    '2': step2Total > 0 ? Math.min(1, step2Filled / step2Total) : 0,
    '3': step3Total > 0 ? Math.min(1, step3Filled / step3Total) : 0,
    '4': step4Total > 0 ? Math.min(1, step4Filled / step4Total) : 0,
  };

  function getStructurePlaceholderByContentType() {
    const raw = (formData.contentType || '').toString().toLowerCase();

    const isProduct =
      raw.includes('fiche produit') ||
      raw.includes('product') ||
      raw.includes('produit');

    const isCategory =
      raw.includes('page catégorie') ||
      raw.includes('page categorie') ||
      raw.includes('catégorie') ||
      raw.includes('categorie') ||
      raw.includes('category page') ||
      raw.includes('long_category') ||
      raw.includes('long category');

    const isStoreLocator =
      raw.includes('store locator') ||
      raw.includes('magasin') ||
      raw.includes('store_locator');

    const isGmb =
      raw === 'gmb' ||
      raw.includes('google my business');

    if (isCategory) {
      return (
        t('H1: Nom de la catégorie – Notre sélection de mot-clé principal') + '\n' +
        t('H2: Présentation de la catégorie') + '\n' +
        t('H2: Découvrez nos différents types de produits ou services') + '\n' +
        t('H3: Sous-catégorie 1') + '\n' +
        t('H3: Sous-catégorie 2') + '\n' +
        t("H2: Guide d’achat – Comment bien choisir ?") + '\n' +
        t('H2: Pourquoi faire confiance à la marque ?')
      );
    }

    if (isStoreLocator) {
      return (
        t('H1: Trouver un magasin Nom de l’enseigne') + '\n' +
        t('H2: Notre réseau de magasins') + '\n' +
        t('H2: Comment trouver le magasin le plus proche ?') + '\n' +
        t('H2: Nos implantations par région') + '\n' +
        t('H3: Région 1') + '\n' +
        t('H3: Région 2') + '\n' +
        t('H2: Services disponibles en magasin') + '\n' +
        t('H2: Préparer votre visite') + '\n' +
        t('H2: Questions fréquentes sur nos magasins')
      );
    }

    if (isGmb) {
      return (
        t('Titre avec Nom établissement  + catégorie') + '\n' +
        t('Adresse & contact : Une phrase simple avec adresse et numéro de téléphone') + '\n' +
        t('Description courte (accroche impactante, bénéfices immédiats)') + '\n' +
        t('Description moyenne (structurée, orientée bénéfices, solutions pour le client)') + '\n' +
        t('Produits phares (liste)') + '\n' +
        t('Horaires')
      );
    }

    if (isProduct) {
      return (
        t('H1: Nom du produit + bénéfice principal') + '\n' +
        t('H2: Présentation du produit') + '\n' +
        t('H2: Caractéristiques et détails techniques') + '\n' +
        t('Liste à puces') + '\n' +
        t("H2: Avantages pour l’utilisateur") + '\n' +
        t('H2: Pourquoi choisir ce produit ?') + '\n' +
        t("H2: À qui s’adresse ce produit ?") + '\n' +
        t("H2: Conseils d’utilisation") + '\n' +
        t('H2: Questions fréquentes')
      );
    }

    return (
      t('H1: Titre principal de l’article') + '\n' +
      t('H2: Introduction') + '\n' +
      t('H3: Présentation du sujet') + '\n' +
      t('H3: Objectif ou intérêt pour le lecteur') + '\n' +
      t('H2: Développement principal') + '\n' +
      t('H3: Concept') + '\n' +
      t('H3: Fonctionnement') + '\n' +
      t('H3: Exemples généraux') + '\n' +
      t('H2: Applications') + '\n' +
      t('H3: Cas pratiques') + '\n' +
      t('H3: Conseils ou recommandations') + '\n' +
      t('H3: Points à retenir') + '\n' +
      t('H2: Informations complémentaires') + '\n' +
      t('H3: Données ou faits généraux (sans inventer)') + '\n' +
      t('H3: Questions fréquentes / FAQ (optionnel)') + '\n' +
      t('H3: Résumé des points clés') + '\n' +
      t('H3: Ouverture / pistes de réflexion ou appel à l’action')
    );
  }

  const isGenerateAgentDisabled = !canGenerateAgent || isGeneratingAgent;
  const showGenerateAgentTooltip = !canGenerateAgent && !isGeneratingAgent;

  const blockReadonlyEdits = (event) => {
    if (!isReadOnly) return;
    if (event.target.closest('.submit-button1, .agent-step-index, .agent-step-btn')) return;

    const isMeta = event.metaKey || event.ctrlKey;
    const allowedKeys = ['Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown', 'Escape'];
    if (allowedKeys.includes(event.key)) return;
    if (isMeta && ['c', 'a', 'C', 'A'].includes(event.key)) return;
    event.preventDefault();
  };

  const blockReadonlyClick = (event) => {
    if (!isReadOnly) return;
    if (event.target.closest('.submit-button1, .agent-step-index, .agent-step-btn')) return;
    if (event.target.closest('button, label, .objective-card, .MuiAutocomplete-root, .upload-dropzone, .kb-url-add-btn, .kb-url-pill-remove, a')) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const renderGenerateAgentButton = () => isReadOnly ? null : (
    <span className="agent-generate-agent-button-wrapper">
      <button
        type="button"
        className="agent-generate-agent-button"
        onClick={handleGenerateAgent}
        disabled={isGenerateAgentDisabled}
      >
        {isGeneratingAgent ? (
          <>
            <Spinner size="sm" className="me-2" />
            {t('Génération en cours...')}
          </>
        ) : (
          t('Générer mon agent')
        )}
      </button>
      {showGenerateAgentTooltip && (
        <span className="agent-generate-agent-tooltip" role="tooltip">
          {t('Renseignez le rôle et le type de contenu pour générer automatiquement')}
        </span>
      )}
    </span>
  );

  const renderSectionHeader = (iconSrc, iconAlt, title, headerClassName = 'mb-2') => (
    <div className={`agent-section-header agent-section-header-with-action ${headerClassName}`}>
      <div className="agent-section-header-main">
        <img src={iconSrc} alt={iconAlt} className="agent-section-icon" />
        <div className="agent-section-title">{title}</div>
      </div>
      {renderGenerateAgentButton()}
    </div>
  );

  return (
    <div
      className={`p-4 agent-page-container${isReadOnly ? ' agent-details-readonly' : ''}`}
      onKeyDown={blockReadonlyEdits}
      onBeforeInput={isReadOnly ? (event) => event.preventDefault() : undefined}
      onPaste={isReadOnly ? (event) => event.preventDefault() : undefined}
      onDrop={isReadOnly ? (event) => { event.preventDefault(); event.stopPropagation(); } : undefined}
      onClickCapture={blockReadonlyClick}
    >
      {isSubmitting && (
        <div className="agent-creating-overlay">
          <div className="agent-creating-modal">
            <div className="agent-creating-title">
              {isEditMode
                ? t('Mise à jour de votre agent IA en cours…')
                : t('Création de votre agent IA en cours…')}
            </div>
            <p className="agent-creating-text">
              {isEditMode
                ? t('Nous appliquons vos modifications. Cela ne prendra que quelques secondes.')
                : t('Nous configurons ses paramètres. Cela ne prendra que quelques secondes.')}
            </p>
          </div>
        </div>
      )}
      {isGeneratingAgent && (
        <div className="agent-creating-overlay">
          <div className="agent-creating-modal">
            <div className="agent-creating-title">{t('Génération de votre agent IA en cours…')}</div>
            <p className="agent-creating-text">
              {t('Nous préparons la structure, le ton et les règles éditoriales de votre agent.')}
            </p>
          </div>
        </div>
      )}
      <div className="agent-page-header-sticky">
        <div
          className="d-flex align-items-center justify-content-between mb-2"
          style={{ marginTop: '-30px', marginLeft: '-25px' }}
        >
          <div>
            <span style={{ fontWeight: 600, color: 'black', fontSize: '16px', cursor: 'pointer' }} onClick={() => history.push(agentListPath)} >{t('Agent IA')}</span>
            <span style={{ fontSize: '14px' }}> &gt; </span>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              {formData.nomAgent?.trim() || t("Nom de l'agent")}
            </span>
          </div>
          {!isReadOnly && (
            <button
              type="button"
              className="submit-button"
              onClick={handleSubmitWithInlineEditsCheck}
              disabled={!isFormValid}
            >
              {isEditMode ? t('Mettre à jour') : t('Sauvegarder')}
            </button>
          )}
        </div>
        <hr style={{ margin: '0 -10px 16px -55px', width: 'calc(100% + 80px)', borderTop: '1px solid rgb(200, 202, 206)' }} />
      </div>

      <div className={`agent-page-layout${helpPanel.open ? ' help-open' : ''}`}>
        <div className="agent-page-main">
          <div className="agent-dashboard-header1" style={{ borderBottom: 'none', paddingBottom: 0, marginBottom: '1.5rem' }}>
            <img
              src={agentImageUrl}
              alt={formData.nomAgent?.trim() || t("Agent IA")}
              className="agent-dashboard-avatar"
            />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <h3 className="agent-dashboard-name" style={{ margin: 0 }}>
                  {formData.nomAgent?.trim() || t("Nom de l'agent")}
                </h3>
                <span className={`agent-dashboard-badge1 ${missionBadgeClass}`}>{missionLabel}</span>
              </div>
              <p className="agent-dashboard-role-text" style={{ marginBottom: 0 }}>
                {formData.selectedWebsite?.urlSite || formData.selectedWebsite?.url || ''}
                {missionLabel === t('Traduction') ? (
                  <>
                    {formData.langue && (
                      <> • {t('Langue de traduction')}: {formData.langue.language || formData.langue.name || ''}</>
                    )}
                  </>
                ) : (
                  <>
                    {formData.objectif && <> • {t('Rôle')}: {formData.objectif}</>}
                    {formData.contentType && <> • {t('Type de contenu')}: {displayedContentTypeLabel}</>}
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="agent-steps-nav">
            {tabs.map((tab, idx) => {
              const tabNumber = Number(tab.id);
              const isFuture = tabNumber > currentStepNumber;
              const progress = tabProgressMap[tab.id] || 0;
              const visualProgress = isFuture ? 0 : progress;
              const isCompleted = !isFuture && visualProgress >= 1;
              const isPartial = !isFuture && visualProgress > 0 && visualProgress < 1;
              const isActive = activeTab === tab.id;
              const isLast = idx === tabs.length - 1;
              return (
                <div
                  key={tab.id}
                  className={`agent-step-btn${isActive ? ' active' : ''}${isCompleted ? ' completed' : ''}${isFuture ? ' future' : ''}`}
                >
                  <div className="agent-step-visual">
                    <span
                      className={`agent-step-index${isCompleted ? ' completed' : ''}${isPartial ? ' partial' : ''}`}
                      style={{ '--step-progress': `${Math.round(visualProgress * 100)}%` }}
                      role="button"
                      tabIndex={0}
                      onClick={() => setActiveTab(tab.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setActiveTab(tab.id);
                        }
                      }}
                      aria-label={`${t('Aller à l’étape')} ${tab.id}: ${tab.label}`}
                    >
                      {isCompleted ? '✓' : tab.id}
                    </span>
                    {!isLast && (
                      <>
                        <span className="agent-step-connector before-chevron" aria-hidden="true" />
                        <span className="agent-step-chevron" aria-hidden="true">›</span>
                        <span className="agent-step-connector after-chevron" aria-hidden="true" />
                      </>
                    )}
                  </div>
                  <span className="agent-step-label">{tab.label}</span>
                </div>
              );
            })}
          </div>

          {showStepGuideBadge && !isReadOnly && (
            <div className="agent-step-guide-badge">
              <span className="agent-step-guide-title-icon" aria-hidden="true">💡</span>
              <div className="agent-step-guide-content">
                <div className="agent-step-guide-title">
                  {t('Étape')} {currentStepNumber} — {currentStepLabel}
                </div>
                <p className="agent-step-guide-text">{stepGuideTextByTab[activeTab]}</p>
                <button
                  type="button"
                  className="agent-step-guide-link"
                  onClick={() => startCreateAgentiaTourFromTab(activeTab)}
                >
                  {t('Voir le guide complet de cette étape')} →
                </button>
              </div>
              <button
                type="button"
                className="agent-step-guide-close"
                aria-label={t('Fermer')}
                onClick={() => setShowStepGuideBadge(false)}
              >
                ×
              </button>
            </div>
          )}

          {/* Mission block */}
          {activeTab === '1' && (
            <div className="agent-mission-block mb-4" id="agentia-create-step1-mission">
              <div className="agent-mission-text">
                <div className="agent-mission-header">
                  <img src="/Images/mission-icon.png" alt="Mission" className="agent-mission-icon" />
                  <div className="agent-mission-title">{t("Mission de l'agent")}</div>
                </div>
                <div className="objective-cards agent-mission-cards">
                  <div
                    className={`objective-card ${formData.mission === t('Rédaction') ? 'selected' : ''}`}
                    onClick={() => {
                      handleChange('mission', t('Rédaction'));
                      try { localStorage.setItem('agentMissionType', t('Rédaction')); } catch { }
                    }}
                  >
                    <div className="card-check">
                      {formData.mission === t('Rédaction') ? (
                        <div className="checkmark-circle">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      ) : (
                        <div className="empty-circle"></div>
                      )}
                    </div>
                    <div className="card-content">
                      <div className="card-title-wrapper">
                        <img src="/Images/redact-icon.png" alt="Rédaction" className="card-title-icon" />
                        <h3 className="card-title">{t('Rédaction')}</h3>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`objective-card ${formData.mission === t('Traduction') ? 'selected' : ''} ${!isTranslationEnabled ? 'objective-card-disabled' : ''}`}
                    onClick={() => {
                      if (!isTranslationEnabled) return;
                      handleChange('mission', t('Traduction'));
                      try { localStorage.setItem('agentMissionType', t('Traduction')); } catch { }
                    }}
                  >
                    <div className="card-check">
                      {formData.mission === t('Traduction') ? (
                        <div className="checkmark-circle">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      ) : (
                        <div className="empty-circle"></div>
                      )}
                    </div>
                    <div className="card-content">
                      <div className="card-title-wrapper">
                        <img src="/Images/tradu-icon.png" alt="Traduction" className="card-title-icon" />
                        <h3 className="card-title">{t('Traduction')}</h3>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="agent-mission-subtitle">
                  {formData.mission === t('Traduction') && !isTranslationEnabled
                    ? t("La mission Traduction n'est pas activée pour votre abonnement.")
                    : t("L'agent générera du contenu original à partir de vos consignes.")}
                </div>
              </div>
            </div>
          )}

          {/* Tab Content */}
          <div className={`agent-tab-content${(activeTab === '3' || activeTab === '4') ? ' agent-tab-content-plain' : ''}`}>

            {/* Section 1 : Informations générales */}
            {activeTab === '1' && (
              <div>
                {renderSectionHeader('/Images/gen-icon.png', 'Informations générales', t('Informations générales'))}

                <div className="mb-3" id="agentia-create-step2-nom-agent">
                  <div className="label-title">{t('Nom de l\'agent')}*</div>
                  <CustomAutocompleteListAndFreeSolo
                    disabled={false}
                    list={[]}
                    value={formData.nomAgent}
                    placeholder={t('Nommez votre agent comme un membre de votre équipe')}
                    showDropdownIcon={false}
                    setValue={(val) => {
                      if (typeof val === 'string') handleChange('nomAgent', val);
                      else if (val && typeof val === 'object') handleChange('nomAgent', val.name || '');
                      else handleChange('nomAgent', '');
                    }}
                    getOptionLabel={(option) => option?.name || ''}
                    onInputChange={(text) => handleChange('nomAgent', text)}
                  />
                </div>

                <div className="mb-3">
                  <div className="label-title">{t('Site web')}*</div>
                  <CustomAutocomplete
                    disabled={websiteList.length <= 0}
                    list={websiteList}
                    value={formData.selectedWebsite}
                    setValue={(option) => handleChange('selectedWebsite', option)}
                    groupBy={() => false}
                    getOptionSelected={(option) => option.id === formData.selectedWebsite?.id}
                    getOptionDisabled={() => false}
                    getOptionLabel={(option) => option?.urlSite || ''}
                  />
                </div>

                {formData.mission !== t('Traduction') && (
                  <>
                    <div className="mb-3" id="agentia-create-step3-role">
                      <div className="label-title">{t('Rôle')}*</div>
                      <CustomAutocompleteListAndFreeSolo
                        disabled={false}
                        list={roleList}
                        value={formData.objectif}
                        placeholder={t('Sélectionnez et personnalisez...')}
                        showDropdownIcon={true}
                        setValue={(val) => {
                          if (typeof val === 'string') handleChange('objectif', val);
                          else if (val && typeof val === 'object') handleChange('objectif', val.name);
                          else handleChange('objectif', '');
                        }}
                        getOptionLabel={(option) => option?.name || ''}
                        onInputChange={(text) => handleChange('objectif', text)}
                      />
                    </div>

                    <div className="mb-3" id="agentia-create-step4-type-contenu">
                      <div className="label-title">{t('Type de contenu')}*</div>
                      {isOnlyOtherContentType ? (
                        <InputField
                          label=""
                          value={formData.contentType}
                          onChange={(val) => handleChange('contentType', val)}
                          placeholder={t('Saisissez un type de contenu personnalisé')}
                        />
                      ) : (
                        <CustomAutocompleteListAndFreeSolo
                          disabled={false}
                          list={contentTypeList}
                          value={formData.contentType}
                          placeholder={t('Sélectionnez un type de contenu')}
                          showDropdownIcon={true}
                          setValue={(val) => {
                            if (typeof val === 'string') handleChange('contentType', val);
                            else if (val && typeof val === 'object') handleChange('contentType', val.name || val.type || '');
                            else handleChange('contentType', '');
                          }}
                          getOptionLabel={(option) => option?.name || ''}
                          onInputChange={(text) => handleChange('contentType', text)}
                        />
                      )}
                    </div>
                  </>
                )}

                <div className="d-flex justify-content-end mt-4">
                  <div className="next-step-group">

                    <button
                      type="button"
                      className="submit-button1"
                      onClick={() => setActiveTab('2')}
                      disabled={!isReadOnly && !isGeneralStepValid}
                    >
                      {t('Suivant')}
                      <img src="/Images/next-icon.png" alt="right-arrow" style={{ width: '16px', height: '16px', marginLeft: '10px' }} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Section 2 : Ton & Audience */}
            {activeTab === '2' && (
              <div>
                {renderSectionHeader('/Images/ton-audi-icon.png', 'Ton & audience', t('Ton & audience'))}
                <div className="agent-section-description mb-3">
                  <p>
                    {t("Définissez le positionnement éditorial de votre agent afin qu'il adapte son ton,sa cible et son vocabulaire à votre activité.")}

                  </p>
                </div>

                {formData.mission === t('Traduction') ? (
                  <div className="mb-3">
                    <div className="label-title">{t('Langue de traduction')}*</div>
                    <CustomAutocomplete
                      disabled={availableLanguagesList.length <= 0}
                      list={availableLanguagesList}
                      value={formData.langue}
                      setValue={(option) => handleChange('langue', option)}
                      groupBy={() => false}
                      getOptionSelected={(option) => option?.id === formData.langue?.id}
                      getOptionDisabled={() => false}
                      getOptionLabel={(option) => option?.language || ''}
                    />
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <div className="label-title">{t('Langue')}*</div>
                      <CustomAutocomplete
                        disabled={availableLanguagesList.length <= 0}
                        list={availableLanguagesList}
                        value={formData.langue}
                        setValue={(option) => handleChange('langue', option)}
                        groupBy={() => false}
                        getOptionSelected={(option) => option?.id === formData.langue?.id}
                        getOptionDisabled={() => false}
                        getOptionLabel={(option) => option?.language ? t(option.language) : ''}
                      />
                    </div>

                    <div className="mb-3" id="agentia-create-step5-tonalite">
                      <div className="label-title">{t('Tonalité')}*</div>
                      <CustomChipsAutocomplete
                        callbackFromMultiSelect={(newList) => handleChange('tonalite', newList)}
                        value={Array.isArray(formData.tonalite) ? formData.tonalite : []}
                        disabled={false}
                        list={TONALITE_PRESET_LIST}
                        placeholder={t('Sélectionnez et personnalisez...')}
                        setValue={(option) => handleChange('tonalite', option)}
                        getOptionLabel={(option) =>
                          typeof option === 'string' ? t(option) : t(option.thematic) || option.thematic || ''
                        }
                      />
                    </div>

                    <div className="mb-3" id="agentia-create-step6-cible">
                      <div className="label-title">{t('Cible / Audience')}*</div>
                      <CustomAutocompleteListAndFreeSolo
                        list={[
                          { name: t('Grand public') }, { name: t('Professionnels / B2B') },
                          { name: t('Jeunes adultes (18–25 ans)') }, { name: t('Adultes (25–40 ans)') },
                          { name: t('Seniors (60+)') }, { name: t('Parents') },
                          { name: t('Étudiants / Universitaires') }, { name: t('Entreprises / Startups') },
                          { name: t('Influenceurs / Créateurs de contenu') }, { name: t('Utilisateurs débutants / novices') },
                          { name: t('Experts / professionnels du secteur') }, { name: t('Clients fidèles') },
                          { name: t('Voyageurs / touristes') }, { name: t('Passionnés de mode / lifestyle') },
                          { name: t('Amateurs de technologie / geek') },
                        ]}
                        value={formData.cible}
                        placeholder={t('Sélectionnez et personnalisez...')}
                        showDropdownIcon={true}
                        setValue={(val) => {
                          if (typeof val === 'string') handleChange('cible', val);
                          else if (val && typeof val === 'object') handleChange('cible', val.name);
                          else handleChange('cible', '');
                        }}
                        getOptionLabel={(option) => option?.name || ''}
                        onInputChange={(text) => handleChange('cible', text)}
                      />
                    </div>

                    <div className="mb-3" id="agentia-create-step7-type-produit-service">
                      <InputField
                        label={t('Type de produits / services proposés par le site*')}
                        value={formData.TypeProducts}
                        onChange={(val) => handleChange('TypeProducts', val)}
                        placeholder={t('Ex: prêt-à-porter féminin')}
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Section 3 : Structure & Rédaction */}
            {activeTab === '3' && (
              <div className="agent-structure-section">
                {formData.mission !== t('Traduction') && (
                  <>
                    <div className="agent-content-block">
                      {renderSectionHeader('/Images/struc-redac.png', 'Construction du contenu', t('Construction du contenu'), 'mb-3')}

                      <SuggestTextarea
                        fieldKey="structureTexte"
                        fieldLabel={t('Structure attendu')}
                        onEditingChange={handleInlineEditEditingChange}
                        onRegisterFocus={registerInlineEditFocusHandler}
                        label={(
                          <>
                            <span>{t('Structure attendu')}</span>
                            <button
                              type="button"
                              className="creation-help-btn"
                              onClick={() => openHelpPanel('structureAttendue', formData.contentType)}
                            >
                              <img src="/Images/icon-help.png" alt="help" />
                              <span>{t('Aide à la création')}</span>
                            </button>
                          </>
                        )}
                        value={formData.structureTexte}
                        onValidate={(val) => handleChange('structureTexte', val)}
                        placeholder={structurePlaceholder}
                        suggestion={structurePlaceholder}
                        maxLength={5000}
                      />

                      <SuggestTextarea
                        fieldKey="RedacRules"
                        fieldLabel={t('Règles rédactionnelles')}
                        onEditingChange={handleInlineEditEditingChange}
                        onRegisterFocus={registerInlineEditFocusHandler}
                        label={(
                          <>
                            <span>{t('Règles rédactionnelles')}</span>
                            <button type="button" className="creation-help-btn" onClick={() => openHelpPanel('redactionRules')}>
                              <img src="/Images/icon-help.png" alt="help" />
                              <span>{t('Aide à la création')}</span>
                            </button>
                          </>
                        )}
                        value={formData.RedacRules}
                        onValidate={(val) => handleChange('RedacRules', val)}
                        placeholder={t('Niveau de langue :\n' +
                          t('Relation au lecteur :') + '\n' +
                          t('Tutoiement :') + '\n' +
                          t('Rythme :') + '\n' +
                          t('Voix :') + '\n' +
                          t('Posture :') + '\n' +
                          t('Objectif :') + '\n' +
                          t('Style :') + '\n' +
                          t('Clarté :'))}
                        suggestion={
                          t('Niveau de langue :') + '\n' +
                          t('Relation au lecteur :') + '\n' +
                          t('Tutoiement :') + '\n' +
                          t('Rythme :') + '\n' +
                          t('Voix :') + '\n' +
                          t('Posture :') + '\n' +
                          t('Objectif :') + '\n' +
                          t('Style :') + '\n' +
                          t('Clarté :') + '\n'
                        }
                        maxLength={5000}
                      />
                    </div>

                    <div className="agent-lexical-block mt-4">
                      <div className="agent-section-header mb-2">
                        <img src="/Images/lexi-icon.png" alt="Cadre lexical" className="agent-section-icon" />
                        <div className="agent-section-title">{t('Cadre lexical')}</div>
                      </div>

                      <div id="agentia-create-step8-recommanded-lexique">
                        <TagInput
                          label={(
                            <>
                              <span>{t('Lexique recommandé')}</span>
                              <button type="button" className="creation-help-btn" onClick={() => openHelpPanel('recommandedLexique')}>
                                <img src="/Images/icon-help.png" alt="help" />
                                <span>{t('Aide à la création')}</span>
                              </button>
                            </>
                          )}
                          value={formData.recommandedLexique}
                          onChange={(val) => handleChange('recommandedLexique', val)}
                          placeholder={t('Ajouter un terme ou une expression')}
                          placeholderFontSize="16px"
                          variant="green"
                          maxLength={4000}
                        />
                      </div>

                      <div id="agentia-create-step9-interdit-expressions">
                        <TagInput
                          label={(
                            <>
                              <span>{t('Expressions interdites')}</span>
                              <button type="button" className="creation-help-btn" onClick={() => openHelpPanel('interditExpressions')}>
                                <img src="/Images/icon-help.png" alt="help" />
                                <span>{t('Aide à la création')}</span>
                              </button>
                            </>
                          )}
                          value={formData.InterditExpressions}
                          onChange={(val) => handleChange('InterditExpressions', val)}
                          placeholder={t('Ajouter un terme ou une expression')}
                          placeholderFontSize="16px"
                          variant="red"
                          maxLength={4000}
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="agent-advanced-block mt-4">
                  {formData.mission === t('Traduction')
                    ? renderSectionHeader('/Images/persoo-icon.png', 'Personnalisation avancée', t('Personnalisation avancée'))
                    : (
                      <div className="agent-section-header mb-2">
                        <img src="/Images/persoo-icon.png" alt="Personnalisation avancée" className="agent-section-icon" />
                        <div className="agent-section-title">{t('Personnalisation avancée')}</div>
                      </div>
                    )}

                  <SuggestTextarea
                    fieldKey="instructions"
                    fieldLabel={t('Consignes complémetaires')}
                    onEditingChange={handleInlineEditEditingChange}
                    onRegisterFocus={registerInlineEditFocusHandler}
                    label={(
                      <>
                        <span>{t('Consignes complémetaires')}</span>
                        <button type="button" className="creation-help-btn" onClick={() => openHelpPanel('instructionsPersonnalisation')}>
                          <img src="/Images/icon-help.png" alt="help" />
                          <span>{t('Aide à la création')}</span>
                        </button>
                      </>
                    )}
                    value={formData.instructions}
                    onValidate={(val) => handleChange('instructions', val)}
                    placeholder={
                      t('Angle à privilégier :') + '\n' +
                      t('Point à approfondir :') + '\n' +
                      t('Élément à inclure :') + '\n'
                    }
                    suggestion={
                      t('Angle à privilégier :') + '\n' +
                      t('Point à approfondir :') + '\n' +
                      t('Élément à inclure :') + '\n'
                    }
                    maxLength={5000}
                  />

                  <div className="d-flex justify-content-between mt-4">
                    <button type="button" className="submit-button1" onClick={() => setActiveTab('2')}>
                      {t('Précédent')}
                    </button>
                    <button type="button" className="submit-button1" onClick={() => setActiveTab('4')}>
                      {t('Suivant')}
                      <img src="/Images/next-icon.png" alt="right-arrow" style={{ width: '16px', height: '16px', marginLeft: '10px' }} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Section 4 : Base de connaissances */}
            {activeTab === '4' && (
              <div>
                <div className="agent-content-block">
                  {renderSectionHeader('/Images/conai-icon.png', 'Base de connaissance locale', t('Base de connaissance locale'))}
                  <div className="agent-section-description mb-3">
                    <p>{t("Optionnel : vos ressources internes aident l’agent à produire des contenus plus précis et personnalisés.\n")}</p>
                   
                  </div>
                  <div
                    className={`upload-dropzone${isKnowledgeBaseLimitReached ? ' is-limit-reached' : ''}`}
                    id="agentia-create-step11-kb-local"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleFileDrop}
                  >
                    <input
                      type="file"
                      id="upload-file__input"
                      multiple
                      disabled={isKnowledgeBaseLimitReached}
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                    <div className="upload-dropzone-inner">
                      <div className="upload-dropzone-icon">
                        <img src="/Images/imp-icon.png" alt="Upload" />
                      </div>
                      <div className={`upload-dropzone-text-main${isKnowledgeBaseLimitReached ? ' is-limit-message' : ''}`}>
                        {isKnowledgeBaseLimitReached
                          ? t('Vous avez atteint le nombre maximum de fichiers autorisés pour cet agent.')
                          : t('Glissez-déposez votre fichier')}
                      </div>
                      {!isKnowledgeBaseLimitReached && (
                        <div className="upload-dropzone-text-sub">{t('ou cliquez pour parcourir')}</div>
                      )}
                      <label
                        htmlFor={isKnowledgeBaseLimitReached ? undefined : 'upload-file__input'}
                        className={`upload-dropzone-button${isKnowledgeBaseLimitReached ? ' is-disabled' : ''}`}
                      >
                        {t('Parcourir les fichiers')}
                      </label>
                      {/* Fichiers déjà enregistrés en BDD (mode édition) */}
                      {existingKnowledgeBaseNames.length > 0 && (
                        <div className="upload-dropzone-filelist">
                          {existingKnowledgeBaseNames.map((name, idx) => (
                            <div
                              key={`existing-${name}-${idx}`}
                              className="upload-dropzone-filename-row"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginTop: 6,
                                padding: '6px 0',
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 13,
                                  color: '#111827',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '210px',
                                }}
                                title={name}
                              >
                                {name}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveExistingKbName(idx);
                                }}
                                style={{
                                  border: 'none',

                                  cursor: 'pointer',
                                  padding: 4,
                                  marginLeft: 8,
                                  borderRadius: 9999,
                                }}
                                aria-label={t('Supprimer le fichier existant')}
                              >
                                <img
                                  src="/Images/close-iconnn.png"
                                  alt={t('Supprimer')}
                                  style={{ width: 14, height: 14 }}
                                />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Liste des fichiers importés (nouveaux fichiers ajoutés dans cette session) */}
                      {knowledgeBaseFiles.length > 0 && (
                        <div className="upload-dropzone-filelist">
                          {knowledgeBaseFiles.map((file, idx) => (
                            <div
                              key={`${file.name}-${idx}`}
                              className="upload-dropzone-filename-row"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginTop: 6,
                                padding: '6px 0',
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 13,
                                  color: '#111827',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '210px',
                                }}
                                title={file.name}
                              >
                                {file.name}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveFile(idx);
                                }}
                                style={{
                                  border: 'none',

                                  cursor: 'pointer',
                                  padding: 4,
                                  marginLeft: 8,
                                  borderRadius: 9999,
                                }}
                                aria-label={t('Supprimer le fichier')}
                              >
                                <img
                                  src="/Images/close-iconnn.png"
                                  alt={t('Supprimer')}
                                  style={{ width: 14, height: 14 }}
                                />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {knowledgeBaseFileError ? (
                    <div className="upload-dropzone-error">{knowledgeBaseFileError}</div>
                  ) : null}
                </div>

                <div className="agent-content-block mt-4">
                  <div className="agent-section-header mb-2">
                    <img src="/Images/online-icon.png" alt="Base de connaissance" className="agent-section-icon" />
                    <div className="agent-section-title">{t('Base de connaissance web')}</div>
                  </div>
                  <div className="agent-section-description mb-3">
                    <p>{t("Optionnel : ajoutez des URLs (site, blog, concurrents…) pour guider l’agent : il pourra s’en inspirer, analyser leur structure et produire des contenus plus pertinents et alignés avec votre univers.")}</p>
                  </div>

                  <div className="kb-online-fullwidth" id="agentia-create-step12-kb-web">
                    <KnowledgeBaseUrlsInput
                      value={formData.urls}
                      onChange={(val) => handleChange('urls', val)}
                      t={t}
                    />
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <button type="button" className="submit-button1" onClick={() => setActiveTab('3')}>
                      {t('Précédent')}
                    </button>
                    {!isReadOnly && (
                      <button
                        type="button"
                        className="submit-button"
                        onClick={handleSubmitWithInlineEditsCheck}
                        disabled={!isFormValid}
                      >
                        {isEditMode ? t('Mettre à jour') : t('Sauvegarder')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === '2' && (
              <div className="d-flex justify-content-between align-items-center mt-4">
                <button type="button" className="submit-button1" onClick={() => setActiveTab('1')}>
                  {t('Précédent')}
                </button>
                <div className="next-step-group">

                  <button
                    type="button"
                    className="submit-button1"
                    onClick={() => setActiveTab('3')}
                    disabled={!isReadOnly && !isTonAudienceStepValid}
                  >
                    {t('Suivant')}
                    <img src="/Images/next-icon.png" alt="right-arrow" style={{ width: '16px', height: '16px', marginLeft: '10px' }} />
                  </button>
                </div>
              </div>
            )}

            {activeTab === '3' && null}
          </div>

          <div className="box-button-bt d-flex align-items-center justify-content-center mt-4"></div>

          <QuitProjectCreationModal
            isOpen={inlineEditsWarningOpen}
            onClose={() => setInlineEditsWarningOpen(false)}
            onPrimaryAction={() => {
              setInlineEditsWarningOpen(false);
              const firstKey = inlineEditsWarningKeys[0];
              const focusHandler = inlineEditsFocusHandlersRef.current[firstKey];
              if (typeof focusHandler === 'function') {
                requestAnimationFrame(() => focusHandler());
              }
            }}
            onSecondaryAction={() => setInlineEditsWarningOpen(false)}
            title={t('Des modifications sont en attente')}
            message={(
              <>
                {t('Veuillez valider ou annuler les modifications du ou des champs suivants avant de continuer :')} {' '} < br />
                <strong style={{ fontWeight: 600 }}>
                  {inlineEditsWarningKeys.map((key) => inlineEditsInProgress[key] || key).join(', ')}
                </strong>
              </>
            )}
            suggestion=""
            primaryButtonLabel={t('Valider les champs')}
            secondaryButtonLabel={t('Annuler')}
            showSuggestion={false}
            showSecondaryButton={true}
          />

          <ProjectCreatedModal
            isOpen={isInsufficientCreditsModalOpen}
            onClose={() => setIsInsufficientCreditsModalOpen(false)}
            title={t('Crédits insuffisants')}
            message={`${t('Crédits insuffisants pour générer automatiquement un agent.')}\n\n${t('Crédits disponibles')} : ${
              insufficientCreditsDisplay !== null ? insufficientCreditsDisplay : '—'
            }`}
            confirmButtonText={t('Ajouter des crédits')}
            onConfirm={() => {
              setIsInsufficientCreditsModalOpen(false);
              setIsBuyCreditsModalOpen(true);
            }}
            secondaryButtonText={t('Fermer')}
            onSecondaryAction={() => setIsInsufficientCreditsModalOpen(false)}
          />
          <BuyCreditsModal
            open={isBuyCreditsModalOpen}
            onClose={() => setIsBuyCreditsModalOpen(false)}
          />

          <ErrorSuccesModal
            open={errorSuccess}
            message={errorSuccessMsg}
            handleClose={() => setErrorSuccess(false)}
            success={isSuccess}
            toggle={() => setErrorSuccess(!errorSuccess)}
            imageUrl={errorSuccessImg}
          />

          <Joyride
            steps={createAgentiaTourSteps}
            run={runCreateAgentiaTour}
            stepIndex={tourStepIndex}
            continuous
            showSkipButton
            spotlightClicks={true}
            spotlightPadding={5}
            disableOverlayClose={true}
            disableCloseOnEsc={false}
            callback={handleCreateAgentiaTourCallback}
            styles={{
              options: {
                zIndex: 1000,
                primaryColor: "#0064d2",
              },
              overlay: {
                zIndex: 1000,
                backgroundColor: "rgba(15, 23, 42, 0.45)",
              },
              spotlight: {
                zIndex: 1001,
                borderRadius: 8,
                boxShadow: "0 0 0 3px rgba(0, 100, 210, 0.25)",
              },
              tooltip: {
                zIndex: 1002,
                borderRadius: 12,
                padding: 16,
                boxShadow: "0 12px 30px rgba(15, 23, 42, 0.18)",
                width: "auto",
                maxWidth: "none",
                display: "inline-block",
              },
            }}
            locale={{
              last: t("Terminer"),
              next: t("Suivant"),
              back: t("Précédent"),
              skip: t("Passer"),
              close: t("Fermer"),
            }}
          />
        </div>

        <HelpPanel
          open={helpPanel.open}
          field={helpPanel.field}
          onClose={closeHelpPanel}
          contentType={helpPanel.contentType}
        />
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────
   SOUS-COMPOSANT : SuggestTextarea
───────────────────────────────────────────── */
function SuggestTextarea({
  label,
  value,
  onValidate,
  placeholder,
  suggestion,
  maxLength,
  tooltipId,
  tooltipText,
  fieldKey,
  fieldLabel,
  onEditingChange,
  onRegisterFocus,
}) {
  const { t } = useTranslation();
  const textareaRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState('');

  const resize = (el, val) => {
    if (!el) return;
    const cs = window.getComputedStyle(el);
    const lh = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.5 || 20;
    const pt = parseFloat(cs.paddingTop) || 0;
    const pb = parseFloat(cs.paddingBottom) || 0;
    const bt = parseFloat(cs.borderTopWidth) || 0;
    const bb = parseFloat(cs.borderBottomWidth) || 0;
    const minH = lh * 3 + pt + pb + bt + bb;

    if (!val || val.trim() === '') {
      el.style.height = minH + 'px';
      el.style.minHeight = minH + 'px';
      return;
    }
    el.style.height = 'auto';
    el.style.height = Math.max(el.scrollHeight, minH) + 'px';
    el.style.minHeight = minH + 'px';
  };

  useLayoutEffect(() => {
    if (isEditing) resize(textareaRef.current, draftValue);
  }, [draftValue, isEditing]);


  useEffect(() => {
    if (typeof onEditingChange === 'function' && fieldKey) {
      onEditingChange(fieldKey, fieldLabel, isEditing);
    }
  }, [fieldKey, fieldLabel, isEditing, onEditingChange]);


  const focusDraft = () => {
    const el = textareaRef.current;
    if (!el) return;


    el.style.borderColor = '#6458F1';
    el.style.borderStyle = 'solid';
    el.style.borderWidth = '2px';
    el.style.boxShadow = '0 0 0 0.2rem rgba(100, 88, 241, 0.18)';

    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch { }

    el.focus();
    resize(el, isEditing ? draftValue : (value || ''));

    setTimeout(() => {
      if (!textareaRef.current) return;
      textareaRef.current.style.borderColor = '';
      textareaRef.current.style.borderWidth = '';
      textareaRef.current.style.borderStyle = '';
      textareaRef.current.style.boxShadow = '';
    }, 2500);
  };

  useEffect(() => {
    if (typeof onRegisterFocus === 'function' && fieldKey) {
      onRegisterFocus(fieldKey, focusDraft);
    }
  }, [fieldKey, onRegisterFocus, focusDraft]);

  const handleOpen = () => {
    const initialDraft =
      value && value.trim()
        ? value
        : (placeholder || suggestion || '');
    setDraftValue(initialDraft);
    setIsEditing(true);
    if (typeof onEditingChange === 'function' && fieldKey) {
      onEditingChange(fieldKey, fieldLabel, true);
    }
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        resize(textareaRef.current, initialDraft);
      }
    }, 0);
  };

  const handleValidate = () => {
    onValidate(draftValue);
    setIsEditing(false);
    if (typeof onEditingChange === 'function' && fieldKey) {
      onEditingChange(fieldKey, fieldLabel, false);
    }
  };

  const handleCancel = () => {
    setDraftValue('');
    setIsEditing(false);
    if (typeof onEditingChange === 'function' && fieldKey) {
      onEditingChange(fieldKey, fieldLabel, false);
    }
  };

  const currentLength = (draftValue || '').length;
  const maxLengthValue = maxLength || 50000;
  const hasValue = value && value.trim();

  return (
    <div className="mb-3">
      <div className="label-title">
        {label}
        {tooltipId && (
          <span id={tooltipId} className="ms-2" style={{ cursor: 'pointer', color: '#ef4444' }}>ⓘ</span>
        )}
      </div>

      <div className="suggest-edit-wrapper">
        <textarea
          ref={textareaRef}
          id={fieldKey ? `agentia-create-guide-textarea-${fieldKey}` : undefined}
          className="form-control form-ct auto-resize-textarea"
          value={isEditing ? draftValue : (value || '')}
          onChange={(e) => {
            if (!isEditing) return;
            setDraftValue(e.target.value);
            resize(textareaRef.current, e.target.value);
          }}
          maxLength={maxLength}
          rows={3}
          placeholder={!hasValue ? placeholder : ''}
          style={{
            minHeight: '80px',
            resize: 'none',
            cursor: isEditing ? 'text' : 'pointer',
          }}
          onClick={() => {
            if (!isEditing) handleOpen();
          }}
        />
        {isEditing && (
          <div className="suggest-edit-actions">
            <button type="button" onClick={handleValidate} className="suggest-btn-validate">
              {t('Valider')}
            </button>
            <button type="button" onClick={handleCancel} className="suggest-btn-cancel">
              <img src="/Images/close-iconnn.png" alt={t('Fermer')} />
            </button>
          </div>
        )}
      </div>

      {isEditing && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
          <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>
            {currentLength}/{maxLengthValue} {t('caractères')}
          </span>
        </div>
      )}

      {tooltipId && tooltipText && (
        <UncontrolledTooltip placement="right" target={tooltipId} className="custom-redac-tooltip">
          {tooltipText}
        </UncontrolledTooltip>
      )}
    </div>
  );
}



function InputField({ label, value, onChange, placeholder }) {
  return (
    <div className="mb-3">
      <label className="label-title">{label}</label>
      <CustomAutocompleteListAndFreeSolo
        disabled={false}
        list={[]}
        value={value}
        placeholder={placeholder}
        showDropdownIcon={false}
        setValue={(val) => {
          if (typeof val === 'string') onChange(val);
          else if (val && typeof val === 'object') onChange(val.name || '');
          else onChange('');
        }}
        getOptionLabel={(option) => option?.name || ''}
        onInputChange={(text) => onChange(text)}
      />
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder, maxLength }) {
  const { t } = useTranslation();
  const textareaRef = useRef(null);

  const resize = () => {
    const el = textareaRef.current;
    if (!el) return;

    const cs = window.getComputedStyle(el);
    const lh = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.5 || 20;
    const pt = parseFloat(cs.paddingTop) || 0;
    const pb = parseFloat(cs.paddingBottom) || 0;
    const bt = parseFloat(cs.borderTopWidth) || 0;
    const bb = parseFloat(cs.borderBottomWidth) || 0;

    // Nombre de lignes à prendre en compte :
    // - si value existe → on se base sur le contenu réel (scrollHeight)
    // - sinon → on se base sur le placeholder (nb de lignes du texte d'exemple)
    if (!value || value.trim() === '') {
      const placeholderLines = (placeholder || '').split('\n').length || 1;
      const lineCount = Math.max(3, placeholderLines); // minimum 3 lignes comme avant
      const minH = lh * lineCount + pt + pb + bt + bb;
      el.style.height = minH + 'px';
      el.style.minHeight = minH + 'px';
      return;
    }

    el.style.height = 'auto';
    const minH = lh * 3 + pt + pb + bt + bb;
    el.style.height = Math.max(el.scrollHeight, minH) + 'px';
    el.style.minHeight = minH + 'px';
  };

  useLayoutEffect(() => { resize(); }, [value]);
  useEffect(() => { requestAnimationFrame(() => setTimeout(resize, 100)); }, []);

  const currentLength = (value || '').length;
  const maxLengthValue = maxLength || 50000;

  return (
    <div className="mb-3">
      <label className="label-title">{label}</label>
      <textarea
        ref={textareaRef}
        value={value || ''}
        onChange={(e) => { onChange(e); resize(); }}
        placeholder={placeholder}
        maxLength={maxLength}
        className="form-control form-ct auto-resize-textarea"
        rows={3}
        style={{ minHeight: '60px' }}
      />
      <div style={{ textAlign: 'right', marginTop: '4px', fontSize: '0.75rem', color: '#6c757d' }}>
        {currentLength}/{maxLengthValue} {t('caractères')}
      </div>
    </div>
  );
}

function AutoResizeTextarea({ label, value, onChange, placeholder, maxLength, tooltipId, tooltipText }) {
  const { t } = useTranslation();
  const textareaRef = useRef(null);

  const resize = () => {
    const el = textareaRef.current;
    if (!el) return;
    if (!value || value.trim() === '') {
      const cs = window.getComputedStyle(el);
      const lh = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.5 || 20;
      const pt = parseFloat(cs.paddingTop) || 0;
      const pb = parseFloat(cs.paddingBottom) || 0;
      const bt = parseFloat(cs.borderTopWidth) || 0;
      const bb = parseFloat(cs.borderBottomWidth) || 0;
      const minH = lh * 3 + pt + pb + bt + bb;
      el.style.height = minH + 'px';
      el.style.minHeight = minH + 'px';
      return;
    }
    el.style.height = 'auto';
    const cs = window.getComputedStyle(el);
    const lh = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.5 || 20;
    const pt = parseFloat(cs.paddingTop) || 0;
    const pb = parseFloat(cs.paddingBottom) || 0;
    const bt = parseFloat(cs.borderTopWidth) || 0;
    const bb = parseFloat(cs.borderBottomWidth) || 0;
    const minH = lh * 3 + pt + pb + bt + bb;
    el.style.height = Math.max(el.scrollHeight, minH) + 'px';
    el.style.minHeight = minH + 'px';
  };

  useLayoutEffect(() => { resize(); }, [value]);
  useEffect(() => { requestAnimationFrame(() => setTimeout(resize, 100)); }, []);

  const currentLength = (value || '').length;
  const maxLengthValue = maxLength || 50000;

  return (
    <div className="mb-3">
      <div className="label-title">
        {label}
        {tooltipId && (
          <span id={tooltipId} className="ms-2" style={{ cursor: 'pointer', color: '#ef4444' }}>ⓘ</span>
        )}
      </div>
      <textarea
        ref={textareaRef}
        className="form-control form-control-valid form-ct auto-resize-textarea"
        value={value || ''}
        onChange={(e) => { onChange(e); resize(); }}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={3}
        style={{ minHeight: '60px' }}
      />
      <div style={{ textAlign: 'right', marginTop: '4px', fontSize: '0.75rem', color: '#6c757d' }}>
        {currentLength}/{maxLengthValue} {t('caractères')}
      </div>
      {tooltipId && tooltipText && (
        <UncontrolledTooltip placement="right" target={tooltipId} className="custom-redac-tooltip">
          {tooltipText}
        </UncontrolledTooltip>
      )}
    </div>
  );
}

export default CreateAgentIAFromList;